import React, { useEffect, useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Modal, Button, Select, LoadingBlock, Alert } from '@v2/components';
import { useMissionControlVersions, useSetDefaultMissionControlVersion } from '@v2/hooks';
import type { MissionControlVersion } from '@v2/types';
import { EditMissionControlVersionModalProps } from './types';
import { styles } from './styles';

const formatVersionOption = (v: MissionControlVersion): string => {
  const desc = v.description === null || v.description === '' ? 'No description' : v.description;
  const released = moment.utc(v.releaseDate).local().format('M/D/YYYY LT');
  return `${v.isDefault ? '(Default) ' : ''}${v.version} - ${desc} (Released: ${released})`;
};

export const EditMissionControlVersionModal: React.FC<EditMissionControlVersionModalProps> = ({
  isOpen,
  onClose,
  currentVersion,
  onSuccess,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string>(currentVersion ?? '');
  const { versions, loading: versionsLoading } = useMissionControlVersions(isOpen);
  const {
    setDefaultVersion,
    loading: saving,
    error: saveError,
  } = useSetDefaultMissionControlVersion();

  const activeVersions = versions.filter(v => !v.isInactive);
  const versionOptions = activeVersions.map(v => ({
    value: v.version,
    label: formatVersionOption(v),
  }));
  const hasChanged = selectedVersion !== (currentVersion ?? '');

  useEffect(() => {
    if (isOpen) {
      setSelectedVersion(currentVersion ?? '');
    }
  }, [isOpen, currentVersion]);

  const handleSave = async () => {
    const ok = await setDefaultVersion(selectedVersion);
    if (ok) {
      toast.success('Default Mission Control version updated');
      onSuccess();
      onClose();
    } else {
      toast.error(saveError ?? 'Failed to update default Mission Control version');
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Mission Control Version"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !hasChanged}
            isLoading={saving}>
            Save
          </Button>
        </>
      }>
      {versionsLoading ? (
        <LoadingBlock />
      ) : (
        <div className={styles.content}>
          {saveError && <Alert variant="error">{saveError}</Alert>}
          <Select
            label="Mission Control Version"
            options={versionOptions}
            value={selectedVersion}
            onChange={e => setSelectedVersion(e.target.value)}
            aria-label="Mission Control version"
          />
        </div>
      )}
    </Modal>
  );
};

export default EditMissionControlVersionModal;
